import { useEffect, useState } from "react";
import { Menu, LogOut, Check, X, Loader2, Download, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Monogram } from "@/components/Monogram";
import { SectionView } from "@/components/SectionView";
import { guestApi, errMsg } from "@/lib/api";
import { Hero, Carousel } from "@/guest/HomeHero";
import Registration from "@/guest/Registration";
import StayView from "@/guest/StayView";

const SectionHead = ({ eyebrow, title, text }) => (
  <div className="mb-8">
    <div className="eyebrow">{eyebrow}</div>
    <h2 className="font-serif-d mt-3 text-4xl leading-tight sm:text-5xl">{title}</h2>
    {text && <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-slate-600">{text}</p>}
  </div>
);

const Block = ({ id, children }) => (
  <section id={id} className="mx-auto max-w-4xl scroll-mt-24 border-t border-[var(--line)] px-5 py-16 sm:py-24" data-testid={`section-${id}`}>{children}</section>
);

const CmsPage = ({ p }) => (
  <Block id={p.slug}>
    <SectionHead eyebrow={p.eyebrow || p.menu_label} title={p.title} />
    <div className={`space-y-8 ${p.slug === "contacts" || p.slug === "downloads" ? "!space-y-3" : ""}`}>
      {p.sections.map((sec) => <SectionView key={sec.id} section={sec} />)}
    </div>
  </Block>
);

const Rsvp = ({ family, onChange }) => {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const decline = async () => {
    setBusy(true);
    try {
      await guestApi.post("/guest/rsvp", { rsvp: "declined", message: msg });
      onChange({ ...family, rsvp: "declined" });
      setOpen(false);
      toast.success("Thank you for letting us know.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <a href="#registration-form" className="ivory-card group p-6 transition-colors hover:border-[var(--gold)]" data-testid="rsvp-yes-btn">
        <Check className="text-[var(--gold-deep)]" /><div className="mt-3 text-lg">Yes, we'll be there</div><div className="text-sm text-slate-500">Continue to registration</div>
      </a>
      <button onClick={() => setOpen(true)} className="ivory-card p-6 text-left transition-colors hover:border-[var(--gold)]" data-testid="rsvp-no-btn">
        <X className="text-slate-400" /><div className="mt-3 text-lg">Sorry, we can't make it</div>
        <div className="text-sm text-slate-500">{family.rsvp === "declined" ? "You've let the couple know" : "Let the couple know"}</div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--ivory)]" data-testid="decline-dialog">
          <DialogTitle className="font-serif-d text-2xl font-normal">We'll miss you</DialogTitle>
          <textarea className="input-lux min-h-[110px]" placeholder="A note for the couple (optional)" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="decline-message-input" />
          <button onClick={decline} disabled={busy} className="btn-ink" data-testid="decline-confirm-btn">{busy && <Loader2 size={16} className="animate-spin" />} Send to the couple</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const useInstall = () => {
  const [evt, setEvt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  return evt;
};

export default function GuestSite({ onSignOut }) {
  const [data, setData] = useState(null);
  const [menu, setMenu] = useState(false);
  const install = useInstall();
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;

  useEffect(() => {
    guestApi.get("/guest/me").then(({ data: d }) => setData(d)).catch((e) => {
      if (e?.response?.status === 401) { onSignOut(); } else { toast.error(errMsg(e)); }
    });
  }, [onSignOut]);

  if (!data) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-[var(--gold)]" /></div>;
  const { family, settings: s, pages, fields } = data;
  const home = pages.find((p) => p.slug === "home");
  const others = pages.filter((p) => p.slug !== "home");
  const cut = others.findIndex((p) => p.slug === "costume") + 1;
  const before = others.slice(0, cut);
  const after = others.slice(cut);
  const submitted = !!family.submitted_at;
  const lbl = (p) => [p.slug, p.menu_label || p.title];
  const slugs = new Set(pages.map((p) => p.slug));
  const kids = (slug) => others.filter((p) => p.parent === slug && slugs.has(slug)).map(lbl);
  const top = (list) => list.filter((p) => !p.parent || !slugs.has(p.parent)).map((p) => [...lbl(p), kids(p.slug)]);
  const links = [["home", "Home", []], ...top(before), ["rsvp", "RSVP", []], ...(submitted ? [["stay", "Stay & Transfers", []]] : []), ...top(after)];
  const setFamily = (f) => setData((d) => ({ ...d, family: { ...d.family, ...f } }));

  return (
    <div className="min-h-screen overflow-x-clip bg-[var(--ivory)]" data-testid="guest-site">
      {data.preview && (
        <div className="relative flex flex-wrap items-center justify-center gap-x-3 bg-[var(--wine)] px-4 py-2 text-center text-xs text-[var(--ivory)]" data-testid="guest-preview-banner">
          <span>Organizer preview of <b>{family.name}</b>. This is what they see. Changes are not saved.</span>
          <button onClick={() => window.close()} className="underline underline-offset-4" data-testid="guest-preview-close-btn">Close preview</button>
        </div>
      )}
      <header className="sticky top-0 z-40 border-b border-[var(--line)] bg-[rgba(250,246,238,.85)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="#home" className="flex items-center gap-3" data-testid="header-brand">
            <Monogram size={40} testId="header-monogram" />
            <span className="font-script text-3xl">{s.short_names}</span>
          </a>
          <nav className="hidden items-center gap-6 lg:flex">
            {links.map(([id, l, sub]) => (sub.length ? (
              <div key={id} className="group relative" data-testid={`nav-group-${id}`}>
                <a href={`#${id}`} className="inline-flex items-center gap-1 text-sm text-slate-600 transition-colors hover:text-[var(--ink)]" data-testid={`nav-${id}`}>{l} <ChevronDown size={13} className="transition-transform group-hover:rotate-180" /></a>
                <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <div className="min-w-[200px] rounded-2xl border border-[var(--line)] bg-[var(--ivory)] p-2 shadow-xl" data-testid={`nav-submenu-${id}`}>
                    {sub.map(([sid, sl]) => <a key={sid} href={`#${sid}`} className="block rounded-xl px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-[var(--ivory-2)] hover:text-[var(--ink)]" data-testid={`nav-${sid}`}>{sl}</a>)}
                  </div>
                </div>
              </div>
            ) : <a key={id} href={`#${id}`} className="text-sm text-slate-600 transition-colors hover:text-[var(--ink)]" data-testid={`nav-${id}`}>{l}</a>))}
          </nav>
          <button className="rounded-full p-2 lg:hidden" onClick={() => setMenu(true)} aria-label="Menu" data-testid="mobile-menu-btn"><Menu /></button>
        </div>
      </header>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent className="bg-[var(--ivory)]" data-testid="mobile-menu">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Monogram size={72} className="mx-auto mt-4" testId="menu-monogram" />
          <div className="mt-8 flex flex-col">
            {links.map(([id, l, sub]) => (
              <div key={id} className="border-b border-[var(--line)]">
                <a href={`#${id}`} onClick={() => setMenu(false)} className="font-serif-d block py-4 text-2xl" data-testid={`mobile-nav-${id}`}>{l}</a>
                {sub.map(([sid, sl]) => <a key={sid} href={`#${sid}`} onClick={() => setMenu(false)} className="-mt-2 block pb-3 pl-5 text-base text-slate-600" data-testid={`mobile-nav-${sid}`}>{sl}</a>)}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <section id="home" className="mx-auto max-w-4xl space-y-8 px-5 pb-16 pt-6 sm:pt-10">
        <Hero s={s} familyName={family.name} />
        <Carousel images={s.carousel} />
        {home?.sections.map((sec) => <SectionView key={sec.id} section={sec} />)}
      </section>

      {before.map((p) => <CmsPage key={p.id} p={p} />)}

      <Block id="rsvp">
        <SectionHead eyebrow="RSVP" title={s.rsvp_heading} />
        <Rsvp family={family} onChange={setFamily} />
        <div className="mt-14" id="registration-form">
          <h3 className="font-serif-d text-3xl sm:text-4xl">{s.reg_heading}</h3>
          {s.reg_intro && <p className="mb-8 mt-3 max-w-2xl text-[17px] leading-relaxed text-slate-600">{s.reg_intro}</p>}
          <Registration family={family} fields={fields} settings={s} onSaved={(f) => setFamily(f)} />
        </div>
      </Block>

      {submitted && (
        <Block id="stay">
          <SectionHead eyebrow="Stay & Transfers" title={s.stay_heading} text={s.stay_intro} />
          <StayView stay={family.stay} />
        </Block>
      )}

      {after.map((p) => <CmsPage key={p.id} p={p} />)}

      <footer className="border-t border-[var(--line)] px-5 py-16 text-center" data-testid="guest-footer">
        <Monogram size={96} className="mx-auto" testId="footer-monogram" />
        <div className="font-script mt-4 text-4xl">{s.short_names}</div>
        <div className="mt-2 text-xs uppercase tracking-[.3em] text-[var(--gold-deep)]">{s.date_text} · {s.location}</div>
        {s.footer_text && <p className="mt-4 text-sm text-slate-500">{s.footer_text}</p>}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {install && <button className="btn-ghost" onClick={() => install.prompt()} data-testid="install-app-btn"><Download size={15} /> Install app</button>}
          <button className="btn-ghost" onClick={onSignOut} data-testid="guest-signout-btn"><LogOut size={15} /> Sign out</button>
        </div>
        {isIos && <p className="mt-4 text-xs text-slate-400" data-testid="ios-install-hint">On iPhone: tap Share, then “Add to Home Screen” to install.</p>}
      </footer>
    </div>
  );
}
